import { inject, Service } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type {
  OnyxConfirmModalData,
  OnyxHttpErrorResponse,
} from '@onyx/angular';
import {
  OnyxConfirmModalComponent,
  OnyxLanguagePipe,
  OnyxModalService,
  OnyxToastService,
} from '@onyx/angular';
import type { Observable, Subject } from 'rxjs';
import { filter, map, of } from 'rxjs';
import { CONFLICT_ERROR_CODE } from '../../../../../common/constants/common/conflict-error-code';
import { VALIDATION_ERROR_CODES } from '../../../../../common/constants/common/validation-error-codes';
import type {
  CompanyDictionaryByCode,
  CompanyDictionaryCode,
  CompanyDictionaryFormByCode,
} from '../enums/company-dictionary-code';

@Service()
export class DictionariesHelper {
  private readonly modalService = inject(OnyxModalService);
  private readonly translateService = inject(TranslateService);
  private readonly languagePipe = inject(OnyxLanguagePipe);
  private readonly toastService = inject(OnyxToastService);

  private readonly I18N = 'dictionaries';

  public confirmEdit(
    code: CompanyDictionaryCode,
    item: CompanyDictionaryByCode[CompanyDictionaryCode],
    cancel$: Subject<void>,
  ): Observable<void> {
    if (!item.hasRelatedObjects) return of(undefined);

    return this.modalService
      .open<OnyxConfirmModalData, boolean>(OnyxConfirmModalComponent, {
        heading: this.translateService.instant(
          `${this.I18N}.confirmEdit.heading.${code}`,
          { name: this.languagePipe.transform(item.names) },
        ),
        message: `${this.I18N}.confirmEdit.message`,
        confirmButtonText: 'labels.saveChanges',
        confirmButtonColor: 'blue',
      })
      .result.pipe(
        filter((ok) => {
          if (ok === false) cancel$.next();
          return !!ok;
        }),
        map(() => undefined),
      );
  }

  public handleEditError(
    response: OnyxHttpErrorResponse,
    code: CompanyDictionaryCode,
    form: CompanyDictionaryFormByCode[CompanyDictionaryCode],
  ): void {
    if (
      VALIDATION_ERROR_CODES.includes(response.status) &&
      response.error.names === CONFLICT_ERROR_CODE
    ) {
      this.toastService.showError({
        message: this.translateService.instant(
          `${this.I18N}.uniqueErrors.${code}`,
          { name: this.languagePipe.transform(form.names) },
        ),
      });
      response.handled = true;
    }

    throw response;
  }
}
